/**
 * MediaLibraryPicker
 *
 * A standalone, framework-agnostic media library picker that returns the selected
 * image's URL via a callback. It is the generic sibling of MediaLibraryPlugin.js
 * (which inserts the picked image directly into TinyMCE). This one is used by the
 * block/template "image" property controls in TinyMCELoader.js and the admin
 * Templates preview modal.
 *
 * Usage:
 *   window.openMediaLibraryPicker({
 *     mediaType: 'image',
 *     onSelect: ({ url, thumbnailUrl, title, mediaId }) => { ... }
 *   })
 *
 * It injects its own Tailwind <dialog> into <body>, loads media from the real
 * GET /admin/media/browse endpoint (folders, search, upload, pagination) and returns
 * the stable public_url for the picked image, then removes itself from the DOM.
 *
 * NOTE: The modal markup and the load/upload logic intentionally mirror
 * MediaLibraryPlugin.js "manual mode" — the manual path is the reliable one and avoids
 * any dependency on the Stimulus media-picker controller being present on the page.
 */

import { uploadErrorMessage, renderUploadFailures, clearUploadFailures } from "../../wps_pages/upload_errors";

const BROWSE_URL = "/admin/media/browse";
const UPLOAD_URL = "/admin/media/uploads";
const PER_PAGE = 24;

// Escape user-controlled values before interpolating into innerHTML (matches the
// pattern used in tools/WYSIWYG.js). Prevents stored XSS via media titles/filenames.
const escapeHtml = (text) =>
    String(text == null ? "" : text).replace(/[&<>"']/g, (m) => ({
        "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#039;"
    }[m]));

// Only allow http(s) or root-relative URLs as image sources (blocks javascript: URIs).
const isSafeUrl = (url) => typeof url === "string" && /^(https?:|\/)/i.test(url);

class MediaLibraryPicker {
    constructor({ mediaType = "image", onSelect = null } = {}) {
        this.mediaType = mediaType;
        this.onSelect = onSelect;
        this.container = null;
        this.modal = null;
        this.currentFolder = null;
        this.page = 1;
        this.hasMore = false;
        this.loading = false;
        this.searchTimer = null;
    }

    get csrfToken() {
        return document.querySelector('meta[name="csrf-token"]')?.content || "";
    }

    open() {
        this.container = document.createElement("div");
        this.modal = this.buildModal(this.container);
        document.body.appendChild(this.container);

        // Backdrop + stacking (matches MediaLibraryPlugin)
        if (!document.getElementById("media-library-picker-style")) {
            const style = document.createElement("style");
            style.id = "media-library-picker-style";
            style.textContent = `
                dialog.media-library-picker-modal::backdrop {
                    background: rgba(0, 0, 0, 0.5);
                    backdrop-filter: blur(4px);
                }
                dialog.media-library-picker-modal { z-index: 9999 !important; }
            `;
            document.head.appendChild(style);
        }

        if (typeof this.modal.showModal === "function") {
            this.modal.showModal();
        }

        this.bindControls();
        this.loadMedia();
    }

    close() {
        if (this.modal && this.modal.open) {
            try { this.modal.close(); } catch (e) { /* ignore */ }
        }
        const container = this.container;
        setTimeout(() => {
            if (container && container.parentNode) {
                container.parentNode.removeChild(container);
            }
        }, 200);
    }

    buildModal(container) {
        const heading = this.mediaType === "image" ? "Select Image" : "Select Media";
        const typeOptions = `
            <option value="">All Types</option>
            <option value="image" ${this.mediaType === "image" ? "selected" : ""}>Images</option>
            <option value="video">Videos</option>
            <option value="document">Documents</option>
            <option value="audio">Audio</option>
        `;

        container.innerHTML = `
            <dialog class="media-library-picker-modal w-full max-w-6xl rounded-xl shadow-2xl p-0"
                    style="z-index: 9999; position: fixed; inset: 0; margin: auto; max-height: 90vh; border: none; width: 100%; max-width: 72rem;">
                <div class="bg-white rounded-xl overflow-hidden">
                    <!-- Header -->
                    <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                        <div>
                            <h2 class="text-xl font-semibold text-gray-900">${heading}</h2>
                            <p class="text-sm text-gray-600">Choose from your media library</p>
                        </div>
                        <button type="button" data-mlp="close" class="text-gray-400 hover:text-gray-600">
                            <i class="fas fa-times text-xl"></i>
                        </button>
                    </div>

                    <!-- Breadcrumb -->
                    <div class="px-6 py-3 bg-gray-50 border-b border-gray-200" data-mlp="breadcrumb">
                        <nav class="flex items-center gap-2 text-sm" aria-label="Breadcrumb">
                            <button type="button" data-mlp="root" class="flex items-center gap-1 text-gray-600 hover:text-green-600 transition-colors">
                                <i class="fas fa-home"></i>
                                <span>All Media</span>
                            </button>
                        </nav>
                    </div>

                    <!-- Upload -->
                    <div class="px-6 py-3 bg-blue-50 border-b border-blue-200">
                        <div class="flex items-center gap-3">
                            <input type="file" multiple data-mlp="uploadInput" class="hidden"
                                   accept="image/*,video/*,audio/*,.pdf,.doc,.docx">
                            <button type="button" data-mlp="uploadBtn"
                                    class="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700">
                                <i class="fas fa-upload"></i>
                                Upload Files
                            </button>
                            <span class="text-xs text-gray-600">Upload files to the current folder</span>
                        </div>
                        <div data-mlp="uploadProgress" class="hidden mt-3">
                            <div class="bg-white rounded-lg border border-gray-200 p-3">
                                <div class="flex items-center justify-between mb-2">
                                    <span class="text-sm font-medium text-gray-700">Uploading files...</span>
                                    <span class="text-xs text-gray-500" data-mlp="uploadStatus">0 / 0</span>
                                </div>
                                <div class="w-full bg-gray-200 rounded-full h-2">
                                    <div data-mlp="uploadBar" class="bg-blue-600 h-2 rounded-full transition-all" style="width: 0%"></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Search + filters -->
                    <div class="px-6 py-4 bg-gray-50 border-b border-gray-200">
                        <div class="flex gap-3">
                            <div class="flex-1">
                                <input type="text" placeholder="Search media..." data-mlp="search"
                                       class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500">
                            </div>
                            <div class="flex-shrink-0">
                                <select data-mlp="typeFilter"
                                        class="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500">
                                    ${typeOptions}
                                </select>
                            </div>
                        </div>
                    </div>

                    <!-- Grid -->
                    <div class="px-6 py-6 overflow-y-auto relative" style="max-height: 500px; min-height: 300px;">
                        <div data-mlp="loading" class="hidden absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-10">
                            <div class="text-center">
                                <i class="fas fa-spinner fa-spin text-4xl text-gray-400 mb-4"></i>
                                <p class="text-gray-600">Loading media...</p>
                            </div>
                        </div>
                        <div data-mlp="emptyState" class="hidden text-center py-12">
                            <i class="fas fa-image text-6xl text-gray-300 mb-4"></i>
                            <h3 class="text-lg font-medium text-gray-900 mb-2">No media found</h3>
                            <p class="text-gray-500 mb-6">Try adjusting your search or filters, or upload new files using the button above</p>
                        </div>
                        <div data-mlp="grid" class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4"></div>
                        <div class="mt-6 text-center">
                            <button type="button" data-mlp="loadMore"
                                    class="hidden inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                                <i class="fas fa-arrow-down"></i>
                                Load More
                            </button>
                        </div>
                    </div>

                    <!-- Footer -->
                    <div class="px-6 py-4 bg-gray-50 border-t border-gray-200">
                        <div class="flex items-center justify-end">
                            <button type="button" data-mlp="cancel"
                                    class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            </dialog>
        `;

        return container.querySelector("dialog");
    }

    $(name) {
        return this.container.querySelector(`[data-mlp="${name}"]`);
    }

    bindControls() {
        this.$("close").addEventListener("click", () => this.close());
        this.$("cancel").addEventListener("click", () => this.close());
        this.$("root").addEventListener("click", () => this.navigate(null));
        this.$("loadMore").addEventListener("click", () => this.loadMore());

        const search = this.$("search");
        search.addEventListener("input", () => {
            clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(() => {
                this.page = 1;
                this.loadMedia();
            }, 250);
        });

        this.$("typeFilter").addEventListener("change", () => {
            this.page = 1;
            this.loadMedia();
        });

        this.$("uploadBtn").addEventListener("click", () => this.$("uploadInput").click());
        this.$("uploadInput").addEventListener("change", (e) => this.handleUpload(e));

        // Close on backdrop click / Esc
        this.modal.addEventListener("click", (e) => {
            if (e.target === this.modal) this.close();
        });
        this.modal.addEventListener("cancel", (e) => {
            e.preventDefault();
            this.close();
        });
    }

    showLoading() { this.$("loading").classList.remove("hidden"); }
    hideLoading() { this.$("loading").classList.add("hidden"); }

    async loadMedia(append = false) {
        if (this.loading) return;
        this.loading = true;
        this.showLoading();

        const params = new URLSearchParams({ page: this.page, per_page: PER_PAGE });
        if (this.currentFolder) params.append("folder", this.currentFolder);

        const typeFilter = this.$("typeFilter").value || this.mediaType;
        if (typeFilter) params.append("media_type", typeFilter);

        const searchVal = this.$("search").value;
        if (searchVal) params.append("search", searchVal);

        try {
            const response = await fetch(`${BROWSE_URL}?${params}`);
            const data = await response.json();

            const grid = this.$("grid");
            if (!append) grid.innerHTML = "";

            // Folders only on the first page
            if (!append && Array.isArray(data.folders)) {
                data.folders.forEach((folder) => grid.appendChild(this.createFolderCard(folder)));
            }
            if (Array.isArray(data.media)) {
                data.media.forEach((media) => grid.appendChild(this.createMediaCard(media)));
            }

            if (!append) this.updateBreadcrumb(data.current_folder);

            this.hasMore = !!data.has_more;
            this.$("loadMore").classList.toggle("hidden", !this.hasMore);

            const emptyState = this.$("emptyState");
            const isEmpty = (data.folders?.length || 0) === 0 && (data.media?.length || 0) === 0;
            emptyState.classList.toggle("hidden", append || !isEmpty);
        } catch (error) {
            console.error("MediaLibraryPicker: failed to load media", error);
        } finally {
            this.hideLoading();
            this.loading = false;
        }
    }

    loadMore() {
        if (this.hasMore && !this.loading) {
            this.page += 1;
            this.loadMedia(true);
        }
    }

    navigate(folderPath) {
        this.currentFolder = folderPath;
        this.page = 1;
        this.loadMedia();
    }

    createFolderCard(folder) {
        const card = document.createElement("div");
        card.className = "relative group cursor-pointer";
        card.innerHTML = `
            <div class="aspect-square bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg overflow-hidden border-2 border-transparent hover:border-blue-500 transition-colors flex items-center justify-center">
                <i class="fas fa-folder text-6xl text-blue-500"></i>
            </div>
            <div class="mt-2">
                <div class="text-sm font-medium text-gray-900 truncate" title="${escapeHtml(folder.name)}">
                    <i class="fas fa-folder text-xs text-blue-500 mr-1"></i>${escapeHtml(folder.name)}
                </div>
                <div class="text-xs text-gray-500">${escapeHtml(folder.count)} item${folder.count !== 1 ? "s" : ""}</div>
            </div>
        `;
        card.addEventListener("click", () => this.navigate(folder.path));
        return card;
    }

    createMediaCard(media) {
        const card = document.createElement("div");
        card.className = "relative group cursor-pointer";
        const publicUrl = media.public_url || media.url || "";
        card.dataset.publicUrl = isSafeUrl(publicUrl) ? publicUrl : "";

        const thumb = media.media_type === "image" && isSafeUrl(media.thumbnail_url)
            ? `<img src="${escapeHtml(media.thumbnail_url)}" alt="${escapeHtml(media.title)}" class="w-full h-full object-cover" loading="lazy">`
            : `<div class="flex items-center justify-center h-full bg-gray-200"><i class="fas fa-file text-3xl text-gray-400"></i></div>`;

        card.innerHTML = `
            <div class="aspect-square bg-gray-100 rounded-lg overflow-hidden border-2 border-transparent hover:border-green-500 transition-colors">
                ${thumb}
            </div>
            <div class="mt-2">
                <div class="text-sm font-medium text-gray-900 truncate" title="${escapeHtml(media.title)}">${escapeHtml(media.title)}</div>
                <div class="text-xs text-gray-500">${escapeHtml(media.file_size_human || "")}</div>
            </div>
        `;

        card.addEventListener("click", () => {
            const url = card.dataset.publicUrl;
            if (url && typeof this.onSelect === "function") {
                this.onSelect({
                    url,
                    thumbnailUrl: media.thumbnail_url || null,
                    title: media.title || "",
                    mediaId: media.id
                });
            }
            this.close();
        });

        return card;
    }

    updateBreadcrumb(currentFolder) {
        const nav = this.$("breadcrumb").querySelector("nav");
        if (!nav) return;

        let html = `
            <button type="button" class="flex items-center gap-1 text-gray-600 hover:text-green-600 transition-colors mlp-crumb-root">
                <i class="fas fa-home"></i><span>All Media</span>
            </button>
        `;

        if (currentFolder) {
            const parts = currentFolder.split("/");
            parts.forEach((part, index) => {
                const path = parts.slice(0, index + 1).join("/");
                const isLast = index === parts.length - 1;
                html += `<i class="fas fa-chevron-right text-xs text-gray-400"></i>`;
                html += isLast
                    ? `<span class="font-medium text-gray-900">${escapeHtml(part)}</span>`
                    : `<button type="button" class="text-gray-600 hover:text-green-600 transition-colors mlp-crumb-folder" data-folder-path="${escapeHtml(path)}">${escapeHtml(part)}</button>`;
            });
        }

        nav.innerHTML = html;
        nav.querySelector(".mlp-crumb-root")?.addEventListener("click", () => this.navigate(null));
        nav.querySelectorAll(".mlp-crumb-folder").forEach((btn) => {
            btn.addEventListener("click", () => this.navigate(btn.dataset.folderPath));
        });
    }

    async handleUpload(event) {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        const progressEl = this.$("uploadProgress");
        const statusEl = this.$("uploadStatus");
        const barEl = this.$("uploadBar");
        progressEl.classList.remove("hidden");
        clearUploadFailures(progressEl);

        let uploaded = 0;
        const total = files.length;
        const failures = [];
        statusEl.textContent = `0 / ${total}`;

        for (const file of files) {
            try {
                const formData = new FormData();
                formData.append("file", file);
                formData.append("title", file.name);
                if (this.currentFolder) formData.append("folder", this.currentFolder);

                const response = await fetch(UPLOAD_URL, {
                    method: "POST",
                    headers: { "X-CSRF-Token": this.csrfToken },
                    body: formData
                });

                // fetch only rejects on a network error, so a 422 has to be checked
                // for explicitly — otherwise a rejected upload counts as a success
                // and the author is told nothing.
                if (!response.ok) {
                    throw new Error(await uploadErrorMessage(response));
                }

                uploaded += 1;
                barEl.style.width = `${(uploaded / total) * 100}%`;
                statusEl.textContent = `${uploaded} / ${total}`;
            } catch (error) {
                console.error("MediaLibraryPicker: upload failed for", file.name, error);
                failures.push({ name: file.name, message: error.message });
            }
        }

        renderUploadFailures(progressEl, failures);

        event.target.value = "";
        setTimeout(() => {
            // Keep the panel up when something failed — it is the only place the
            // author is told why the file is missing from the grid.
            if (failures.length === 0) progressEl.classList.add("hidden");
            barEl.style.width = "0%";
            this.page = 1;
            this.loadMedia();
        }, 800);
    }
}

/**
 * Open the media library and invoke onSelect({ url, thumbnailUrl, title, mediaId })
 * with the picked image's stable public URL.
 */
window.openMediaLibraryPicker = function (options = {}) {
    const picker = new MediaLibraryPicker(options);
    picker.open();
    return picker;
};

export { MediaLibraryPicker };
